import { lazy, Suspense } from 'react';
import { Navigate, useRoutes, type RouteObject } from 'react-router-dom';

import { Routes } from 'config';
import { Loader } from 'components';
import { useLoggerHook } from 'hooks';

import RootLayout from './layout';
import PrivateLayout from './(auth)/layout';

const LoginPage = lazy(() => import('./(public)/login'));
const VerifyPage = lazy(() => import('./(public)/verify'));
const SignUpPage = lazy(() => import('./(public)/sign-up'));
const ForgotPasswordPage = lazy(() => import('./(public)/forgot-password'));
const NotFoundPage = lazy(() => import('./(public)/not-found'));

const DashboardPage = lazy(() => import('./(auth)/dashboard'));
const ContactUsPage = lazy(() => import('./(auth)/contact-us'));
const AdditionalInfoPage = lazy(() => import('./(auth)/additional-info'));
const FormsPage = lazy(() => import('./(auth)/forms/details'));
const ProfilePage = lazy(() => import('./(auth)/profile'));
const ResourcesPage = lazy(() => import('./(auth)/resources/details'));
const ResourcesIndexPage = lazy(() => import('./(auth)/resources'));

const toChildPath = (path: string) => path.replace(/^\//, '');

const publicRoutes: RouteObject[] = [
  {
    element: <LoginPage />,
    path: toChildPath(Routes.Auth.Login),
  },
  {
    element: <SignUpPage />,
    path: toChildPath(Routes.Auth.Signup),
  },
  {
    element: <VerifyPage />,
    path: toChildPath(Routes.Auth.Verify),
  },
  {
    element: <ForgotPasswordPage />,
    path: toChildPath(Routes.Auth.ForgotPassword),
  },
];

const privateRoutes: RouteObject[] = [
  {
    element: <DashboardPage />,
    path: toChildPath(Routes.Dashboard.Home),
  },
  {
    element: <ContactUsPage />,
    path: toChildPath(Routes.ContactUs.Home),
  },
  {
    element: <AdditionalInfoPage />,
    path: toChildPath(Routes.AdditionalInfo.Home),
  },
  {
    element: <FormsPage />,
    path: toChildPath(Routes.Course.Form),
  },
  {
    element: <ProfilePage />,
    path: toChildPath(Routes.Profile.Home),
  },
  {
    element: <ResourcesPage />,
    path: toChildPath(Routes.Course.Resource),
  },
  {
    element: <ResourcesIndexPage />,
    path: toChildPath(Routes.Resources.Home),
  },
];

const routes: RouteObject[] = [
  {
    path: Routes.Home,
    element: <RootLayout />,
    children: [
      {
        index: true,
        element: <Navigate replace to={Routes.Auth.Login} />,
      },
      ...publicRoutes,
      {
        children: privateRoutes,
        element: <PrivateLayout />,
      },
      {
        element: <NotFoundPage />,
        path: toChildPath(Routes.NotFound),
      },
      {
        path: '*',
        element: <Navigate replace to={Routes.NotFound} />,
      },
    ],
  },
];

export const AppRoutes = () => {
  const element = useRoutes(routes);

  useLoggerHook();

  return (
    <Suspense fallback={<Loader isLoading fullScreen />}>{element}</Suspense>
  );
};
